import { gql } from '@apollo/client';
import React, { useMemo, useState } from 'react';
import useAppSelector from '../../app/redux/useAppSelector';
import Button from '../../components/Button';
import type { SeriesAddPost_PostFragment } from '../../graphql/generated/graphqlTypes';
import postPolicyCheck, { PostPolicyAction } from '../../post/postPolicyCheck';
import type { EntitySelectConfig } from '../../search/entitySearch/EntitySelect';
import EntitySelect from '../../search/entitySearch/EntitySelect';

const POST_FRAGMENT = gql`
  fragment SeriesAddPost_post on RootPostFields {
    id
    series {
      id
    }
  }
`;

interface SeriesAddPostProps {
  posts: SeriesAddPost_PostFragment[];
  seriesId?: string;
  onAdd(post: SeriesAddPost_PostFragment): void;
}

const SeriesAddPost = ({ posts, seriesId, onAdd }: SeriesAddPostProps) => {
  const currentUser = useAppSelector((state) => state.session.user);
  const [isAddingPost, setIsAddingPost] = useState(false);

  const config = useMemo<EntitySelectConfig>(
    () => ({
      post: {
        extra: (post) => {
          const isInSeries = !!(post.series && post.series.id !== seriesId);
          const isNotEditable = !postPolicyCheck(
            PostPolicyAction.EditPost,
            // @ts-expect-error Adjust useAllPostsForEntitySearch to include owner
            post,
            currentUser,
          );
          const isOptionDisabled = isInSeries || isNotEditable;

          let warning = null;
          if (isInSeries) {
            warning = `Already in ${
              // @ts-expect-error Adjust useAllPostsForEntitySearch to include name
              post.series?.name ? `"${post.series.name}"` : 'a series'
            }`;
          } else if (isNotEditable) {
            warning = 'Cannot edit this post';
          }

          return {
            disabled: isOptionDisabled,
            isDisabled: isOptionDisabled,
            secondary: warning,
          };
        },
        filter: (post) => post.publishedAt,
      },
    }),
    [currentUser, seriesId],
  );

  return isAddingPost ? (
    <EntitySelect
      autoFocus
      isMulti
      controlShouldRenderValue={false}
      value={posts.map((post) => ({ type: 'post', id: post.id }))}
      isClearable={false}
      backspaceRemovesValue={false}
      onChange={(entities) => {
        const last = entities[entities.length - 1];
        if (last?.type === 'post') {
          onAdd(last.object);
          setIsAddingPost(false);
        }
      }}
      onBlur={() => setIsAddingPost(false)}
      config={config}
    />
  ) : (
    <Button variant="secondary" onClick={() => setIsAddingPost(true)}>
      Add post
    </Button>
  );
};

SeriesAddPost.fragments = {
  post: POST_FRAGMENT,
};

export default SeriesAddPost;
